
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MapPin, Euro, Calendar, Zap, PauseCircle, MoreHorizontal, ArrowRightCircle, CheckCircle2, Archive, Trash2, User } from 'lucide-react';
import MissionProgressWidget from '../analysis/MissionProgressWidget';
import { MissionDetails } from '../../types';

const formatDate = (raw: string): string => {
    if (!raw) return '–';
    try {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return raw;
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return raw;
    }
};

interface Mission {
    id: string;
    client: string;
    contact_name?: string | null;
    title: string;
    tjm: string;
    location: string;
    status: string;
    matchScore: number;
    lastUpdate: string;
}

interface SortableMissionCardProps {
    mission: Mission;
    activeMenuId: string | null;
    setActiveMenuId: (id: string | null) => void;
    updateMissionStatus: (id: string, status: string) => void;
    deleteMission: (id: string) => void;
    archiveMission: (id: string) => void;
    columnId: string;
    onClick: () => void;
}

export const SortableMissionCard: React.FC<SortableMissionCardProps> = ({
    mission,
    activeMenuId,
    setActiveMenuId,
    updateMissionStatus,
    deleteMission,
    archiveMission,
    columnId,
    onClick
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: mission.id, data: { status: mission.status } });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        touchAction: 'none'
    };

    const isMenuOpen = activeMenuId === mission.id;

    // Helper for match badge style
    const getMatchBadgeStyle = (score: number) => {
        if (score >= 80) return "bg-emerald-100 text-emerald-700 border-emerald-200";
        if (score >= 60) return "bg-amber-100 text-amber-700 border-amber-200";
        return "bg-slate-100 text-slate-500 border-slate-200";
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={(e) => {
                if (isMenuOpen) return;
                onClick();
            }}
            className={`
                group bg-white p-5 rounded-xl border border-slate-200 shadow-sm transition-all duration-200 relative
                hover:shadow-lg hover:-translate-y-1 hover:border-amber-300 cursor-grab active:cursor-grabbing
            `}
        >
            {/* Header */}
            <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center font-bold text-slate-600 text-xs shadow-sm group-hover:border-amber-200 transition-colors">
                        {mission.client.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                        <div className="text-[10px] font-bold text-slate-400 tracking-wide group-hover:text-amber-500 transition-colors">{mission.client}</div>
                        {mission.contact_name && (
                            <div className="flex items-center gap-1 mt-0.5">
                                <User size={9} className="text-indigo-400" />
                                <span className="text-[9px] font-medium text-indigo-500 truncate max-w-[120px]">{mission.contact_name}</span>
                            </div>
                        )}
                    </div>
                    {/* EN PAUSE BADGE */}
                    {mission.status === 'En Pause' && (
                        <span className="ml-1 bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 border border-amber-200">
                            <PauseCircle size={10} /> En Pause
                        </span>
                    )}
                </div>
                <div className="relative">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenuId(isMenuOpen ? null : mission.id);
                        }}
                        onPointerDown={(e) => e.stopPropagation()} // Prevent drag start on button click
                        className={`p-1 rounded-full transition-all ${isMenuOpen ? 'bg-slate-100 text-slate-600 opacity-100' : 'text-slate-300 hover:text-slate-600 opacity-0 group-hover:opacity-100'}`}
                    >
                        <MoreHorizontal size={16} />
                    </button>

                    {/* DROPDOWN MENU - STATUS ACTIONS */}
                    {isMenuOpen && (
                        <div
                            className="absolute right-0 top-full mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1 animate-in zoom-in-95 duration-200"
                            onPointerDown={(e) => e.stopPropagation()}
                        >
                            {columnId === 'prospection' && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); updateMissionStatus(mission.id, 'Qualification'); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                    <ArrowRightCircle size={16} /> Passer en Qualification
                                </button>
                            )}
                            {columnId === 'qualification' && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); updateMissionStatus(mission.id, 'Proposition'); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                                >
                                    <ArrowRightCircle size={16} /> Passer en Proposition
                                </button>
                            )}
                            {columnId === 'proposition' && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); updateMissionStatus(mission.id, 'Negociation'); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                                >
                                    <ArrowRightCircle size={16} /> Passer en Négociation
                                </button>
                            )}
                            {columnId === 'negociation' && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); updateMissionStatus(mission.id, 'Cloture'); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                >
                                    <CheckCircle2 size={16} /> Clôturer
                                </button>
                            )}
                            {columnId === 'cloture' && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); updateMissionStatus(mission.id, 'Prospection'); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                                >
                                    <ArrowRightCircle size={16} /> Réactiver
                                </button>
                            )}
                            <div className="h-px bg-slate-100 my-1"></div>
                            <button
                                onClick={(e) => { e.stopPropagation(); archiveMission(mission.id); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 rounded-lg transition-colors"
                            >
                                <Archive size={16} /> Archiver
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); deleteMission(mission.id); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                                <Trash2 size={16} /> Supprimer
                            </button>
                        </div>
                    )}
                </div>
            </div>


            {/* Body */}
            <h3 className="font-bold text-slate-900 text-base mb-3 line-clamp-2 leading-tight group-hover:text-amber-600 transition-colors">
                {mission.title}
            </h3>

            <div className="flex items-center gap-4 text-xs text-slate-500 font-medium mb-4">
                <div className="flex items-center gap-1.5">
                    <MapPin size={14} className="text-slate-400 group-hover:text-amber-400 transition-colors" />
                    {mission.location ? mission.location.split(',')[0].trim() : 'NC'}
                </div>
                <div className="flex items-center gap-1.5">
                    <Euro size={14} className="text-slate-400 group-hover:text-amber-400 transition-colors" />
                    {mission.tjm || 'NC'}
                </div>
            </div>

            {/* Footer */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between group-hover:border-amber-100 transition-colors">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-400">
                    <Calendar size={12} />
                    {formatDate(mission.lastUpdate)}
                </div>

                {/* Top Match Badge — only shown when a real score exists */}
                {mission.matchScore > 0 && (
                    <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold border flex items-center gap-1 ${getMatchBadgeStyle(mission.matchScore)}`}>
                        <Zap size={10} fill="currentColor" />
                        Top: {mission.matchScore}%
                    </div>
                )}
            </div>
        </div>
    );
};
